import React, { useState, useRef, useEffect } from 'react';
import {
    Typography,
    Grid,
    Box,
    CssBaseline,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
    Modal,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import {
    EditNote,
    Phone,
    Cancel,
    DeleteForever,
    Visibility,
    Download,
} from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import modalStyle from '../Components/ModalStyle';

const Dispositions = () => {
    const formListRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dispositionFilter, setDispositionFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [dispositionGroupData, setDispositionGroupData] = useState([]);
    const [dispositionList, setDispositionList] = useState([]);
    const [dispositionCategory, setDispositionCategory] = useState([]);
    const [subCategorData, setSubCategorData] = useState([]);
    const [categoryName, setCategoryName] = useState([]);
    const [subCategorDataDropDown, setSubCategorDataDropDown] = useState([]);

    const [totalItems, setTotalItems] = useState(0);
    const [openAddDispositionModal, setAddDispositionModal] = useState(false);
    const [openAddDispositionListModal, setAddDispositionListModal] = useState(false);
    const [openAddDispositionCategoryModal, setAddDispositionCategoryModal] = useState(false);
    const [openAddDispositionSubCategoryModal, setAddDispositionSubCategoryModal] = useState(false);
    const [categoryID, setCategoryID] = useState('');
    const [selectedItem, setSelectedItem] = useState('');
    const [openEditDispositionModal, setOpenEditDispositionModal] = useState(false);
    const [openViewDispositionModal, setOpenViewDispositionModal] = useState(false);
    const [newDisposition, setNewDisposition] = useState({
        user_name: '',
        disposition_contact: '',
        disposition: '',
        disposition_info: '',
        call_type: '',
        disposition_time: '',
    });
    const [isSupervisor, setIsSupervisor] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const api = axios.create({ baseURL: global_link });
    const authToken = process.env.AUTH_TOKEN;

    // Determine if current user is a supervisor (download button visible only to supervisors)
    useEffect(() => {
        const userLevel = sessionStorage.getItem('user_level');
        setIsSupervisor(userLevel === 'supervisor' || userLevel === 'admin');
    }, []);

    useEffect(() => {
        fetchData();
        fetchDispositionData();
        fetchDispositionList();
        fetchDispositionCategory();
    }, [searchTerm, dispositionFilter, startDate, endDate, page, rowsPerPage]);

    const fetchData = async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get('/disposition/all', {
                params: {
                    search: searchTerm,
                    disposition: dispositionFilter,
                    start_date: startDate,
                    end_date: endDate,
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                },
            });
            setData(response.data.data);
            setTotalItems(response.data.total);
        } catch (error) {
            console.error('Error fetching Disposition:', error);
        }
    };

    const fetchDispositionData = async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get('/disposition/group', {
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                },
            });
            setDispositionGroupData(response.data);
        } catch (error) {
            console.error('Error fetching Disposition Group:', error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleDispositionFilterChange = (event) => {
        setDispositionFilter(event.target.value);
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const handleOpenAddDispositionModal = () => {
        setAddDispositionModal(true);
    };

    const handleCloseAddDispositionModal = () => {
        setAddDispositionModal(false);
    };

    const handleOpenAddDispositionListModal = () => {
        setAddDispositionListModal(true);
    };

    const handleCloseAddDispositionListModal = () => {
        setAddDispositionListModal(false);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setNewDisposition({ ...newDisposition, [name]: value });
    };

    const handleOpenAddDispositionCategoryModal = () => {
        setAddDispositionCategoryModal(true);
        fetchDispositionCategory();
    };

    const handleCloseAddDispositionCategoryModal = () => {
        setAddDispositionCategoryModal(false);
    };

    const handleCloseAddDispositionSubCategoryModal = () => {
        setAddDispositionSubCategoryModal(false);
    };

    const copyPhoneNumber = (call_from, call_to) => {
        const phoneNumber = call_from.length > 5 ? call_from : call_to;
        const event = new CustomEvent('copyPhoneNumber', { detail: phoneNumber });
        window.dispatchEvent(event);
    };

    const handleDispositionSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.post('/disposition/add', formData, { headers, withCredentials: true })
            .then((response) => {
                setAddDispositionModal(false);
                fetchData();
                fetchDispositionData();
            }).catch((error) => { console.error(error); });
    };

    const handleDispositionEditSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.put('/disposition/edit', formData, { headers, withCredentials: true })
            .then((response) => {
                fetchData();
                fetchDispositionData();
                handleCloseEditDispositionModal();
            }).catch((error) => { console.error(error); });
    };

    const handleOpenEditDispositionModal = (item) => {
        setSelectedItem(item)
        setOpenEditDispositionModal(true);
        fetchDispositionCategory();
    }

    const handleCloseEditDispositionModal = () => {
        setOpenEditDispositionModal(false);
    }

    const handleOpenViewDispositionModal = (item) => {
        setSelectedItem(item)
        setOpenViewDispositionModal(true);
    }

    const handleCloseViewDispositionModal = () => {
        setOpenViewDispositionModal(false);
    }

    const handleInputModChange = (event) => {
        const { name, value } = event.target;
        setSelectedItem((prevFormState) => ({
            ...prevFormState,
            [name]: value,
        }));
    };

    const handleInputCatModChange = (event) => {
        const { name, value } = event.target;
        setSelectedItem((prevFormState) => ({
            ...prevFormState,
            [name]: value,
        }));
        const selectedCategory = dispositionCategory.find(cat => cat.category === value);
        if (selectedCategory) {
            handleShowSubCategoryDropdown(selectedCategory.id);
            setSelectedItem((prevFormState) => ({
                ...prevFormState,
                disposition_category_id: selectedCategory.id,
                [name]: value
            }));
        }
    };

    const fetchDispositionList = async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get('/disposition/list', {
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                    "Content-Type": "application/json",
                },
            });
            setDispositionList(response.data.data);
        } catch (error) {
            console.error('Error fetching Disposition Group:', error);
        }
    };

    const fetchDispositionCategory = async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get('/disposition/category', {
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                    "Content-Type": "application/json",
                },
            });
            setDispositionCategory(response.data.data);
        } catch (error) {
            console.error('Error fetching Disposition Category:', error);
        }
    };

    const handleDispositionListSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(formListRef.current);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.post('/disposition/addlist', formData, { headers, withCredentials: true })
            .then((response) => {
                fetchDispositionList();
            }).catch((error) => { console.error(error); });
    };

    const handleDispositionCategorySubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(formListRef.current);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.post('/disposition/addcategory', formData, { headers, withCredentials: true })
            .then((response) => {
                fetchDispositionCategory();
            }).catch((error) => { console.error(error); });
    };

    const handleDispositionSubCategorySubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(formListRef.current);
        const category_id = formData.get('category_id');
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.post('/disposition/addsubcategory', formData, { headers, withCredentials: true })
            .then((response) => {
                fetchDispositionCategory();
                handleShowSubCategory(category_id, categoryName);
                setCategoryName(categoryName);
            }).catch((error) => { console.error(error); });
    };

    const handleDispositionListDelete = (id) => {
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.delete(`/disposition/deletelist/${id}`, { headers, withCredentials: true })
            .then((response) => {
                fetchDispositionList();
            }).catch((error) => { console.error(error); });
    }

    const handleShowSubCategory = async (id, category) => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get(`/disposition/subcategory/${id}`, {
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                    "Content-Type": "application/json",
                },
            });
            setAddDispositionSubCategoryModal(true);
            setCategoryName(category);
            setSubCategorData(response.data.data);
            setCategoryID(id);
        } catch (error) {
            console.error('Error fetching Disposition Sub Category:', error);
        }
    }

    const handleShowSubCategoryDropdown = async (id) => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get(`/disposition/subcategory/${id}`, {
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                    "Content-Type": "application/json",
                },
            });
            setSubCategorDataDropDown(response.data.data);
        } catch (error) {
            console.error('Error fetching Disposition Sub Category:', error);
        }
    }

    const handleDispositionSubCategoryDelete = async (id, category_id) => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const headers = {
                Authorization: `Bearer ${authyToken}`,
                "Content-Type": "application/json",
            };
            api.delete(`/disposition/deletesubcategory/${category_id}/${id}`, { headers, withCredentials: true })
                .then((response) => {
                    fetchDispositionCategory();
                    handleShowSubCategory(category_id, categoryName);
                    setCategoryName(categoryName);
                }).catch((error) => { console.error(error); });
        } catch (error) {
            console.error('Error fetching Disposition Sub Category:', error);
        }
    }

    const handleDispositionCategoryDelete = async (id) => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const headers = {
                Authorization: `Bearer ${authyToken}`,
                "Content-Type": "application/json",
            };
            api.delete(`/disposition/deletecategory/${id}`, { headers, withCredentials: true })
                .then((response) => {
                    fetchDispositionCategory();
                }).catch((error) => { console.error(error); });
        } catch (error) {
            console.error('Error fetching Disposition Sub Category:', error);
        }
    }

    const handleInputCatChange = (event) => {
        const { name, value } = event.target;
        const selectedCategory = dispositionCategory.find(cat => cat.category === value);
        handleShowSubCategoryDropdown(selectedCategory.id);
    };

    // Helper function to truncate text
    const truncateText = (text, maxLength = 50) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Escape a CSV cell value
    const escapeCsvCell = (value) => {
        const str = (value ?? '').toString().replace(/"/g, '""');
        return `"${str}"`;
    };

    // Download all matching searched/filtered dispositions as CSV (supervisor only)
    const downloadCSV = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const authyToken = localStorage.getItem(authToken);
            // Fetch all matching records (no pagination) using current filters
            const response = await api.get('/disposition/all', {
                params: {
                    search: searchTerm,
                    disposition: dispositionFilter,
                    start_date: startDate,
                    end_date: endDate,
                    limit: 10000, // large limit to cover searched set
                    offset: 0,
                },
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                },
            });
            const exportData = response.data?.data || [];
            if (!exportData.length) {
                alert('No matching records found to download.');
                return;
            }

            const headers = [
                'Name',
                'Contact',
                'Disposition',
                'Details',
                'Call Type',
                'Category',
                'Sub Category',
                'Date',
            ];
            const rows = exportData.map((row) => [
                escapeCsvCell(row.user_name),
                escapeCsvCell(row.disposition_contact),
                escapeCsvCell(row.disposition),
                escapeCsvCell(row.disposition_info),
                escapeCsvCell(row.call_type),
                escapeCsvCell(row.disposition_category),
                escapeCsvCell(row.disposition_sub_category),
                escapeCsvCell(row.disposition_time),
            ]);

            const csvContent =
                headers.map(escapeCsvCell).join(',') +
                '\n' +
                rows.map((r) => r.join(',')).join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const dateStamp = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.setAttribute('download', `dispositions_export_${dateStamp}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Failed to download CSV. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <CssBaseline />
            <Typography variant='h5' sx={{ marginBottom: '15px' }}>
                Dispositions
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px' }}>
                <Box sx={{ padding: 2, display: 'flex', gap: 2 }}>
                    {/* Search Field */}
                    <TextField
                        label="Search"
                        variant="outlined"
                        fullWidth
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search"
                    />

                    {/* Disposition Filter Dropdown */}
                    <FormControl variant="outlined" sx={{ width: '60%!important' }}>
                        <InputLabel id="disposition-filter-label">Filter by Disposition</InputLabel>
                        <Select
                            value={dispositionFilter}
                            onChange={handleDispositionFilterChange}
                            label="Filter by Disposition"
                            labelId="disposition-filter-label"
                        >
                            <MenuItem value="">Select Disposition</MenuItem>
                            {dispositionGroupData.map((row) => (
                                <MenuItem key={row.disposition} value={row.disposition}>
                                    {row.disposition}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Start Date Picker */}
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: '50%!important' }}
                    />

                    {/* End Date Picker */}
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: '50%!important' }}
                    />

                    {/* Add Disposition Button */}
                    <Button
                        variant="contained"
                        type="submit"
                        color='primary'
                        sx={{ padding: '10px', width: '40%!important' }}
                        onClick={handleOpenAddDispositionModal}
                    >
                        Add Disposition
                    </Button>
                    <Button variant="contained" color="error" sx={{ padding: '10px' }} onClick={handleOpenAddDispositionListModal}>
                        <EditNote sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                    </Button>
                    <Button variant="contained" color="secondary" sx={{ padding: '10px' }} onClick={handleOpenAddDispositionCategoryModal}>
                        <EditNote sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                    </Button>
                    {/* CSV Download - visible only to supervisors/admins */}
                    {isSupervisor && (
                        <Button
                            variant="contained"
                            color="success"
                            sx={{ padding: '10px', minWidth: 'auto' }}
                            onClick={downloadCSV}
                            disabled={isDownloading}
                            startIcon={<Download />}
                        >
                            {isDownloading ? 'Downloading...' : 'Download CSV'}
                        </Button>
                    )}
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table sx={{ fontSize: '5.1rem !important', tableLayout: 'fixed' }} size="small">
                        <TableHead sx={{ bgcolor: 'background.table', width: '100%' }}>
                            <TableRow>
                                <TableCell sx={{ width: '10%' }}>Name</TableCell>
                                <TableCell sx={{ width: '12%' }}>Contact</TableCell>
                                <TableCell sx={{ width: '12%' }}>Disposition</TableCell>
                                <TableCell sx={{ width: '12%' }}>Details</TableCell>
                                <TableCell sx={{ width: '10%' }}>Type</TableCell>
                                <TableCell sx={{ width: '18%' }}>Date</TableCell>
                                <TableCell align="center" sx={{ width: '26%' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <Tooltip title={row.user_name}>
                                                <span>{truncateText(row.user_name, 15)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.disposition_contact}>
                                                <span>{truncateText(row.disposition_contact, 18)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.disposition}>
                                                <span>{truncateText(row.disposition, 15)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.disposition_info}>
                                                <span>{truncateText(row.disposition_info, 20)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.call_type}>
                                                <span>{truncateText(row.call_type, 12)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.disposition_time}>
                                                <span>{truncateText(row.disposition_time, 20)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            <Button 
                                                variant="contained" 
                                                color="info" 
                                                sx={{ padding: '1px', margin: '2px' }} 
                                                onClick={() => copyPhoneNumber(row.disposition_contact)}
                                            >
                                                <Phone sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="primary" 
                                                sx={{ padding: '1px', margin: '2px' }} 
                                                onClick={() => handleOpenViewDispositionModal(row)}
                                            >
                                                <Visibility sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button>
                                            <Button 
                                                variant="contained" 
                                                color="warning" 
                                                sx={{ padding: '1px', margin: '2px' }} 
                                                onClick={() => handleOpenEditDispositionModal(row)}
                                            >
                                                <EditNote sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No matching records found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={totalItems}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    labelRowsPerPage="Rows per page"
                />
            </Box>

            {/* Add Disposition Modal */}
            <Modal
                open={openAddDispositionModal}
                onClose={handleCloseAddDispositionModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="modal-title">New Disposition</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseAddDispositionModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <form onSubmit={handleDispositionSubmit}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Disposition Contact"
                                    name="disposition_contact"
                                    onChange={handleInputChange}
                                    fullWidth
                                    variant="outlined"
                                    required="true"
                                    sx={{ marginTop: "5px" }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Disposition Owner"
                                    placeholder="Disposition Owner"
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_owner"
                                    required="true"
                                    sx={{ marginTop: "5px", display: 'none' }}
                                    value={sessionStorage.getItem('extension')}
                                />
                                <TextField
                                    fullWidth
                                    select
                                    label="Call Type"
                                    name="call_type"
                                    sx={{ marginTop: "5px" }}
                                    onChange={handleInputChange}
                                    required="true"
                                >
                                    <MenuItem value="">Select Type Of Call</MenuItem>
                                    <MenuItem value="incoming">Incoming</MenuItem>
                                    <MenuItem value="outgoing">Outgoing</MenuItem>
                                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                                    <MenuItem value="Instagram">Instagram</MenuItem>
                                    <MenuItem value="X">X</MenuItem>
                                    <MenuItem value="Facebook">Facebook</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition Category"
                                    name="disposition_category"
                                    onChange={handleInputCatChange}
                                    sx={{ marginTop: "5px" }}
                                >
                                    <MenuItem disabled selected>Select Category</MenuItem>
                                    {dispositionCategory.map((row) => (
                                        <MenuItem key={row.id} value={row.category}>
                                            {row.category}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition Sub Category"
                                    name="disposition_sub_category"
                                    onChange={handleInputChange}
                                    sx={{ marginTop: "5px" }}
                                >
                                    <MenuItem value="">Select Sub Category</MenuItem>
                                    {subCategorDataDropDown.map((row) => (
                                        <MenuItem key={row.id} value={row.sub_category}>
                                            {row.sub_category}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition"
                                    name="disposition"
                                    sx={{ marginTop: "5px" }}
                                    onChange={handleInputChange}
                                    required="true"
                                >
                                    <MenuItem value="">Select Disposition</MenuItem>
                                    <MenuItem value="Call Back">Call Back</MenuItem>
                                    {dispositionList.map((disposition, i) => (
                                        <MenuItem key={i} value={disposition.disposition_name}>{disposition.disposition_name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    multiline
                                    rows={2}
                                    label="Extra Info"
                                    placeholder="Extra Info"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_info"
                                    required="true"
                                    sx={{ marginTop: "5px" }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color='info' sx={{ padding: "10px", marginTop: "10px", width: "100%" }}>Submit Disposition</Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>

            {/* View Disposition Modal */}
            <Modal
                open={openViewDispositionModal}
                onClose={handleCloseViewDispositionModal}
                aria-labelledby="view-modal-title"
                aria-describedby="view-modal-description"
            >
                <Box sx={{ 
                    ...modalStyle, 
                    maxWidth: '600px',
                    maxHeight: '80vh',
                    overflow: 'auto'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                        <h3 id="view-modal-title">View Disposition Details</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseViewDispositionModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    {selectedItem && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                                    <Typography variant="body1">{selectedItem.user_name || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Contact</Typography>
                                    <Typography variant="body1">{selectedItem.disposition_contact || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Disposition</Typography>
                                    <Typography variant="body1">{selectedItem.disposition || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Call Type</Typography>
                                    <Typography variant="body1">{selectedItem.call_type || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                                    <Typography variant="body1">{selectedItem.disposition_category || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Sub Category</Typography>
                                    <Typography variant="body1">{selectedItem.disposition_sub_category || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                                    <Typography variant="body1">{selectedItem.disposition_time || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Details</Typography>
                                    <Box sx={{ 
                                        bgcolor: 'background.default', 
                                        p: 2, 
                                        borderRadius: 1,
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        <Typography variant="body1">{selectedItem.disposition_info || 'No details provided'}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Edit Disposition Modal */}
            <Modal
                open={openEditDispositionModal}
                onClose={handleCloseEditDispositionModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-contact"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="modal-title">Edit Disposition | {selectedItem && (selectedItem.disposition_contact)}</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseEditDispositionModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <form onSubmit={handleDispositionEditSubmit}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Disposition Contact"
                                    name="disposition_contact"
                                    onChange={handleInputModChange}
                                    fullWidth
                                    variant="outlined"
                                    required="true"
                                    sx={{ marginTop: "5px" }}
                                    value={selectedItem.disposition_contact || ''}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Disposition Owner"
                                    placeholder="Disposition Owner"
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_owner"
                                    required="true"
                                    sx={{ marginTop: "5px", display: 'none' }}
                                    value={sessionStorage.getItem('extension')}
                                />
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_id"
                                    required="true"
                                    sx={{ marginTop: "5px", display: 'none' }}
                                    value={selectedItem.disposition_id || ''}
                                />
                                <TextField
                                    fullWidth
                                    select
                                    label="Call Type"
                                    name="call_type"
                                    sx={{ marginTop: "5px" }}
                                    onChange={handleInputModChange}
                                    value={selectedItem.call_type || ''}
                                    required="true"
                                >
                                    <MenuItem value="">Select Type Of Call</MenuItem>
                                    <MenuItem value="incoming">Incoming</MenuItem>
                                    <MenuItem value="outgoing">Outgoing</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition Category"
                                    name="disposition_category"
                                    onChange={handleInputCatModChange}
                                    value={selectedItem.disposition_category || ''}
                                    sx={{ marginTop: "5px" }}>
                                    <MenuItem disabled selected>Select Category</MenuItem>
                                    {dispositionCategory.map((row) => (
                                        <MenuItem key={row.id} value={row.category}>
                                            {row.category}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition Sub Category"
                                    name="disposition_sub_category"
                                    onChange={handleInputModChange}
                                    value={selectedItem.disposition_sub_category || ''}
                                    sx={{ marginTop: "5px" }}>
                                    <MenuItem value="">Select Sub Category</MenuItem>
                                    {subCategorDataDropDown.map((row) => (
                                        <MenuItem key={row.id} value={row.sub_category}>
                                            {row.sub_category}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Disposition"
                                    name="disposition"
                                    sx={{ marginTop: "5px" }}
                                    onChange={handleInputModChange}
                                    value={selectedItem.disposition || ''}
                                    required="true"
                                >
                                    <MenuItem value="">Select Disposition</MenuItem>
                                    <MenuItem value="Call Back">Call Back</MenuItem>
                                    {dispositionList.map((disposition, i) => (
                                        <MenuItem key={i} value={disposition.disposition_name}>{disposition.disposition_name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    multiline
                                    rows={2}
                                    label="Extra Info"
                                    placeholder="Extra Info"
                                    onChange={handleInputModChange}
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_info"
                                    required="true"
                                    sx={{ marginTop: "5px" }}
                                    value={selectedItem.disposition_info || ''}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color='info' sx={{ padding: "10px", marginTop: "10px", width: "100%" }}>Update Disposition</Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>

            <Modal
                open={openAddDispositionListModal}
                onClose={handleCloseAddDispositionListModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="modal-title">Disposition List</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseAddDispositionListModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <Box sx={{ height: '30vh', overflow: 'auto' }}>
                        <TableContainer>
                            <Table sx={{ fontSize: '5.1rem !important' }} size="small">
                                <TableHead position="sticky" top="-1px" sx={{ bgcolor: 'background.table', width: '100%' }}>
                                    <TableRow>
                                        <TableCell>Disposition</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dispositionList.map((disposition, i) => (
                                        <TableRow key={disposition.id}>
                                            <TableCell>{disposition.disposition_name}</TableCell>
                                            <TableCell><Button variant="contained" color="error" sx={{ padding: '1px', margin: '5px' }} onClick={() => handleDispositionListDelete(disposition.id)}>
                                                <DeleteForever sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <form ref={formListRef} onSubmit={handleDispositionListSubmit}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Disposition List"
                                    placeholder="Disposition List"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    fullWidth
                                    name="disposition_list"
                                    required="true"
                                    sx={{ marginTop: "30px" }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color='info' sx={{ padding: "10px", marginTop: "10px", width: "100%" }}>Submit Disposition List</Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>

            <Modal
                open={openAddDispositionCategoryModal}
                onClose={handleCloseAddDispositionCategoryModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="modal-title">Disposition Category & Sub Category</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseAddDispositionCategoryModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <Box sx={{ height: '30vh', overflow: 'auto' }}>
                        <TableContainer>
                            <Table sx={{ fontSize: '5.1rem !important' }} size="small">
                                <TableHead position="sticky" top="-1px" sx={{ bgcolor: 'background.table', width: '100%' }}>
                                    <TableRow>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Sub Category</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dispositionCategory.map((disposition, i) => (
                                        <TableRow key={disposition.id}>
                                            <TableCell>{disposition.category}</TableCell>
                                            <TableCell> <Button variant="contained" color="secondary" sx={{ padding: '1px', margin: '5px' }} onClick={() => handleShowSubCategory(disposition.id, disposition.category)}>
                                                <EditNote sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button></TableCell>
                                            <TableCell><Button variant="contained" color="error" sx={{ padding: '1px', margin: '5px' }} onClick={() => handleDispositionCategoryDelete(disposition.id)}>
                                                <DeleteForever sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <form ref={formListRef} onSubmit={handleDispositionCategorySubmit}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Disposition Category"
                                    placeholder="Disposition Category"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    fullWidth
                                    name="category"
                                    required="true"
                                    sx={{ marginTop: "30px" }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color='info' sx={{ padding: "10px", marginTop: "10px", width: "100%" }}>Submit Disposition Category</Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>

            <Modal
                open={openAddDispositionSubCategoryModal}
                onClose={handleCloseAddDispositionSubCategoryModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <h3 id="modal-title">Sub Categories For {categoryName}</h3>
                        <Box display="flex" justifyContent="right">
                            <Cancel onClick={handleCloseAddDispositionSubCategoryModal} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Box>
                    <Box sx={{ height: '30vh', overflow: 'auto' }}>
                        <TableContainer>
                            <Table sx={{ fontSize: '5.1rem !important' }} size="small">
                                <TableHead position="sticky" top="-1px" sx={{ bgcolor: 'background.table', width: '100%' }}>
                                    <TableRow>
                                        <TableCell>Sub Category</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subCategorData.map((disposition, i) => (
                                        <TableRow key={disposition.id}>
                                            <TableCell>{disposition.sub_category}</TableCell>
                                            <TableCell><Button variant="contained" color="error" sx={{ padding: '1px', margin: '5px' }} onClick={() => handleDispositionSubCategoryDelete(disposition.id, disposition.category_id)}>
                                                <DeleteForever sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                                            </Button></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <form ref={formListRef} onSubmit={handleDispositionSubCategorySubmit}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Disposition Sub Category"
                                    placeholder="Disposition Category"
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    fullWidth
                                    name="sub_category"
                                    required="true"
                                    sx={{ marginTop: "30px" }}
                                />
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    name="category_id"
                                    required="true"
                                    value={categoryID}
                                    sx={{ display: "none" }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color='info' sx={{ padding: "10px", marginTop: "10px", width: "100%" }}>Submit Disposition Sub Category</Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

export default Dispositions;
